import React, { useState, useEffect } from "react";
import { Filter, ShoppingCart } from "react-feather";
import VisitorNavbar from "../component/VisitorNavbar";
import Footer from "../component/Footer";
import { useParams, Link } from "react-router-dom";
import { baseurl, imageUrl } from "../Baseurl";
import axios from "axios";
import noproduct from "../component/img/product_not_found.jpeg";

const AllProduct = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [uniqueMenus, setUniqueMenus] = useState([]);
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [maxPrice, setMaxPrice] = useState(Infinity);
  const [selectedType, setSelectedType] = useState(false); // for "directSell" filter

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${baseurl}productsByCategory/${slug}`);
      const productList = res.data?.listings ?? res.data ?? [];
      setProducts(productList);
      setFilteredProducts(productList);

      // Extract all dynamic menu keys like menu2, menu3, ..., menuN
      const allMenus = new Set();
      productList.forEach((product) => {
        Object.keys(product).forEach((key) => {
          if (key.startsWith("menu") && key !== "menu1" && product[key]) {
            allMenus.add(product[key]);
          }
        });
      });
      setUniqueMenus([...allMenus]);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [slug]);

  const handleMenuChange = (menu) => {
    setSelectedMenus((prev) =>
      prev.includes(menu) ? prev.filter((m) => m !== menu) : [...prev, menu]
    );
  };

  const handleConditionChange = (condition) => {
    setSelectedConditions((prev) =>
      prev.includes(condition)
        ? prev.filter((c) => c !== condition)
        : [...prev, condition]
    );
  };

  const handlePriceChange = (event) => {
    const price = event.target.value ? parseFloat(event.target.value) : Infinity;
    setMaxPrice(price);
  };

  const handleTypeChange = () => {
    setSelectedType((prev) => !prev);
  };

  const uniqueConditions = [...new Set(products.map((p) => p.condition || "N/A"))];

  useEffect(() => {
    const filtered = products.filter((product) => {
      const matchesCondition =
        selectedConditions.length === 0 ||
        selectedConditions.includes(product.condition || "N/A");

      const matchesPrice = parseFloat(product.price) <= maxPrice;

      const productMenus = Object.keys(product)
        .filter((key) => key.startsWith("menu") && key !== "menu1")
        .map((key) => product[key]);

      const matchesMenus =
        selectedMenus.length === 0 ||
        selectedMenus.some((menu) => productMenus.includes(menu));

      const matchesType = !selectedType || product.type === "directSell";

      return matchesCondition && matchesPrice && matchesMenus && matchesType;
    });

    setFilteredProducts(filtered);
  }, [products, selectedConditions, maxPrice, selectedMenus, selectedType]);

  const getConditionCount = (condition) => {
    return products.filter((p) => (p.condition || "N/A") === condition).length;
  };

  const getMenuCount = (menu) => {
    return products.filter((product) => {
      const productMenus = Object.keys(product)
        .filter((key) => key.startsWith("menu") && key !== "menu1")
        .map((key) => product[key]);
      return productMenus.includes(menu);
    }).length;
  };

  return (
    <>
      <VisitorNavbar />
      <div className="container-fluid py-3">
        <div className="row g-4">
          {/* Filters Section */}
          <div className="col-md-2 hide-on-mobile">
            <h4 className="product-desc-title">
              Filters <Filter size={20} />
            </h4>
            <div className="filter-section p-2 border">
              {/* Condition Filter */}
              <div className="mb-3 text-start">
                <label className="form-label lab1">Condition</label>
                <div>
                  {uniqueConditions.map((condition) => (
                    <div key={condition} className="form-check">
                      <input
                        type="checkbox"
                        id={`condition-${condition}`}
                        className="form-check-input"
                        checked={selectedConditions.includes(condition)}
                        onChange={() => handleConditionChange(condition)}
                      />
                      <label
                        htmlFor={`condition-${condition}`}
                        className="form-check-label text-capitalize"
                      >
                        {condition} ({getConditionCount(condition)})
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Filter */}
              <div className="mb-3 text-start">
                <label className="form-label lab1">Max Price (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter max price"
                  onChange={handlePriceChange}
                />
              </div>

              {/* Type Filter */}
              <div className="mb-3 text-start">
                <label className="form-label lab1">Type</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="type-direct-sell"
                    className="form-check-input"
                    checked={selectedType}
                    onChange={handleTypeChange}
                  />
                  <label htmlFor="type-direct-sell" className="form-check-label">
                    Direct Sell
                  </label>
                </div>
              </div>

              {/* Menu Filters */}
              <div className="mb-3 text-start">
                <label className="form-label lab1">Category</label>
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {uniqueMenus.map((menu) => (
                    <div key={menu} className="form-check">
                      <input
                        type="checkbox"
                        id={`menu-${menu}`}
                        className="form-check-input"
                        checked={selectedMenus.includes(menu)}
                        onChange={() => handleMenuChange(menu)}
                      />
                      <label htmlFor={`menu-${menu}`} className="form-check-label">
                        {menu} ({getMenuCount(menu)})
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="col-md-10">
            <h4 className="product-desc-title">
              Shop Products ({filteredProducts.length})
            </h4>

            {loading ? (
              <div className="d-flex justify-content-center align-items-center vh-50">
                <div className="spinner-border text-danger" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <p className="text-danger">{error}</p>
            ) : (
              <div className="row g-2">
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => (
                    <div className="col-md-3 col-6" key={product.listing_id}>
                      <div className="product-card">
                        <img
                          src={`${imageUrl}listing/${
                            product.images?.[0] || noproduct
                          }`}
                          alt={product.title}
                        />
                        <div className="text-start">
                          <span className="cntn">{product.condition}</span>
                        </div>
                        <h3>{product.title}</h3>
                        <div className="price">
                          <span className="current-price">
                            ₹
                            {Number(product.price).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                        <Link
                          to={`/product/${product.slug}`}
                          className="btn add-to-cart"
                        >
                          <ShoppingCart size={16} /> &nbsp;
                          {product.type === "quoteRequest"
                            ? "View Details"
                            : "View Details"}
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center">
                    <img
                      src={noproduct}
                      className="img-fluid noproductimg"
                      alt="No Product Found"
                    />
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AllProduct;
